// Element CRUD operations extracted from OfferPageBuilderSimple.js lines 1958-2779
import { useCallback, useEffect } from 'react';
import { ELEMENT_TYPES } from '../../../utils/offerElementTypes';
import { LAYOUT_CONFIGS } from '../../../utils/offerLayoutConfigs';

export const useElementOperations = ({
  elements,
  setElements,
  pages,
  setPages,
  selectedElement,
  setSelectedElement,
  selectedElementContext,
  currentPageIndex,
  paginationEnabled,
  editingElement,
  setEditingElement,
  editingField,
  setEditingField,
  tempValue,
  setTempValue,
  deletedElementsHistory,
  setDeletedElementsHistory,
  showUndoNotification,
  setShowUndoNotification,
  contentTabTempValue,
  setContentTabTempValue,
  buttonContentTabTempValue,
  setButtonContentTabTempValue,
  buttonUrlTabTempValue,
  setButtonUrlTabTempValue,
  targetZone,
  setTargetZone,
  draggedElement,
  setDraggedElement,
  setDragOverZone,
  isDragging,
  dragOffset,
  setIsDragging,
  setDragOffset,
  popoverPosition,
  setPopoverPosition,
  setForceRender,
  isResizingRef,
  resizeStartDataRef,
  resizeDirectionRef,
  setIsResizing,
  setResizeDirection,
  setResizeStartData,
  setShowLayoutSelector,
  isBrandsPage,
}) => {
  const generateId = () => `element_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Helper function to recursively find and update nested layouts
  const findAndUpdateLayout = useCallback((elements, layoutId, zoneId, updateFn) => {
    const updateElements = (elementsArray) => {
      let foundInThisLevel = false;

      const result = elementsArray.map(el => {
        // Check if this element is the target layout or image overlay
        if (el.id === layoutId && (el.type === ELEMENT_TYPES.LAYOUT || el.type === ELEMENT_TYPES.IMAGE_OVERLAY)) {
          console.log('✅ Found target container:', layoutId);
          foundInThisLevel = true;
          return {
            ...el,
            zones: el.zones.map(zone => {
              if (zone.id === zoneId) {
                console.log('✅ Found target zone:', zoneId);
                const updatedZone = updateFn(zone);
                console.log('🔄 Zone updated from', zone.elements?.length, 'to', updatedZone.elements?.length, 'elements');
                return updatedZone;
              }
              return zone;
            })
          };
        }

        // If this element is a layout or image overlay and we haven't found the target yet, recursively search its zones
        if (!foundInThisLevel && (el.type === ELEMENT_TYPES.LAYOUT || el.type === ELEMENT_TYPES.IMAGE_OVERLAY) && el.zones) {
          let foundInNested = false;
          const updatedZones = el.zones.map(zone => {
            const recursiveResult = updateElements(zone.elements);
            const wasUpdated = recursiveResult.some((newEl, index) =>
              newEl !== zone.elements[index]
            );

            if (wasUpdated) {
              foundInNested = true;
              return { ...zone, elements: recursiveResult };
            }
            return zone;
          });

          if (foundInNested) {
            console.log('🔄 Updated nested layout in zone');
            foundInThisLevel = true;
            return { ...el, zones: updatedZones };
          }
        }

        return el;
      });

      return result;
    };

    return updateElements(elements);
  }, []);

  const addLayout = useCallback((layoutType) => {
    console.log('🏗️ addLayout called:', { layoutType, targetZone });

    const config = LAYOUT_CONFIGS[layoutType];
    const newLayout = {
      id: generateId(),
      type: ELEMENT_TYPES.LAYOUT,
      layoutType,
      zones: config.zones.map(zone => ({
        ...zone,
        id: `${generateId()}-${zone.id}`,
        elements: []
      })),
      styles: {
        marginBottom: '20px',
        padding: '20px',
        border: '2px dashed #e0e0e0',
        borderRadius: '8px'
      }
    };

    console.log('🆕 Created new layout:', newLayout);

    if (targetZone) {
      console.log('🎯 Adding layout to zone:', targetZone);

      // Update pages if pagination enabled
      if (paginationEnabled) {
        setPages(prevPages => {
          const safeIndex = Math.min(currentPageIndex, prevPages.length - 1);
          return prevPages.map((page, index) => {
            if (index === safeIndex) {
              const result = findAndUpdateLayout(page.elements, targetZone.layoutId, targetZone.zoneId, (zone) => ({
                ...zone,
                elements: [...zone.elements, newLayout]
              }));
              return { ...page, elements: result };
            }
            return page;
          });
        });
      }

      // Add layout to a specific zone (nested layout) - supports deeply nested layouts
      setElements(prev => {
        const result = findAndUpdateLayout(prev, targetZone.layoutId, targetZone.zoneId, (zone) => {
          console.log('✅ Adding nested layout to zone:', zone.id);
          return {
            ...zone,
            elements: [...zone.elements, newLayout]
          };
        });
        console.log('📋 Updated elements array with nested layout:', result);
        return result;
      });
      setTargetZone(null); // Clear target zone
    } else {
      console.log('🎨 Adding layout to main canvas');

      // Update pages if pagination enabled
      if (paginationEnabled) {
        setPages(prevPages => {
          const safeIndex = Math.min(currentPageIndex, prevPages.length - 1);
          return prevPages.map((page, index) =>
            index === safeIndex
              ? { ...page, elements: [...page.elements, newLayout] }
              : page
          );
        });
      }

      // Add layout to main canvas
      setElements(prev => [...prev, newLayout]);
    }

    setShowLayoutSelector(false);
  }, [targetZone, paginationEnabled, currentPageIndex, findAndUpdateLayout]);

  const addElementToZone = useCallback((elementType, layoutId, zoneId) => {
    console.log('🎯 addElementToZone called:', { elementType, layoutId, zoneId });

    // Ignore calls with undefined layoutId or zoneId (can happen during dialog transitions)
    if (!layoutId || !zoneId) {
      console.log('⚠️ Ignoring addElementToZone call with undefined layoutId or zoneId');
      return;
    }

    // Special handling for Layout element - show layout selector dialog for nested layouts
    if (elementType === ELEMENT_TYPES.LAYOUT) {
      console.log('📐 Layout element detected - showing layout selector');
      setTargetZone({ layoutId, zoneId }); // Store where to add the layout
      setShowLayoutSelector(true);
      console.log('🔄 Set showLayoutSelector to true, targetZone:', { layoutId, zoneId });
      return;
    }

    // In brands page mode, use FITTABLE_IMAGE instead of IMAGE for better zoom/positioning functionality
    let actualElementType = elementType;
    if (isBrandsPage && elementType === ELEMENT_TYPES.IMAGE) {
      actualElementType = ELEMENT_TYPES.FITTABLE_IMAGE;
    }

    const { DEFAULT_ELEMENTS } = require('../../../utils/offerDefaultElements');
    const newElement = {
      id: generateId(),
      ...DEFAULT_ELEMENTS[actualElementType]
    };

    console.log('🆕 Creating new element:', newElement);

    // Update pages if pagination enabled
    if (paginationEnabled) {
      setPages(prevPages => {
        const safeIndex = Math.min(currentPageIndex, prevPages.length - 1);
        return prevPages.map((page, index) => {
          if (index === safeIndex) {
            const result = findAndUpdateLayout(page.elements, layoutId, zoneId, (zone) => ({
              ...zone,
              elements: [...zone.elements, newElement]
            }));
            return { ...page, elements: result };
          }
          return page;
        });
      });
    }

    // Use recursive function to find and update the target layout (supports nested layouts)
    setElements(prev => {
      const result = findAndUpdateLayout(prev, layoutId, zoneId, (zone) => ({
        ...zone,
        elements: [...zone.elements, newElement]
      }));

      console.log('📋 Element added to nested layout successfully');
      return result;
    });
  }, [findAndUpdateLayout, paginationEnabled, currentPageIndex, isBrandsPage]);

  // Helper function to delete element from nested layouts
  const deleteElementFromZone = useCallback((elementId, layoutId, zoneId) => {
    console.log('🗑️ Deleting element from zone:', { elementId, layoutId, zoneId });

    // Find the element to be deleted from the zone
    const elementToDelete = elements.find(el => {
      if (el.id === layoutId && el.zones) {
        const zone = el.zones.find(z => z.id === zoneId);
        return zone && zone.elements.some(zoneEl => zoneEl.id === elementId);
      }
      return false;
    });

    if (elementToDelete) {
      const zone = elementToDelete.zones.find(z => z.id === zoneId);
      const zoneElement = zone?.elements.find(el => el.id === elementId);

      if (zoneElement) {
        // Find the original index of the element in the zone
        const originalIndex = zone.elements.findIndex(el => el.id === elementId);

        // Add to deletion history for undo functionality
        console.log('🔄 Adding zone element to deletion history:', zoneElement, 'at index:', originalIndex);
        setDeletedElementsHistory(prev => [...prev, {
          element: zoneElement,
          zone: { layoutId, zoneId },
          originalIndex: originalIndex,
          timestamp: Date.now()
        }]);
        setShowUndoNotification(true);
      }
    }

    // Update pages if pagination enabled
    if (paginationEnabled) {
      setPages(prevPages => {
        return prevPages.map(page => ({
          ...page,
          elements: findAndUpdateLayout(page.elements, layoutId, zoneId, (zone) => ({
            ...zone,
            elements: zone.elements.filter(el => el.id !== elementId)
          }))
        }));
      });
    }

    setElements(prev =>
      findAndUpdateLayout(prev, layoutId, zoneId, (zone) => ({
        ...zone,
        elements: zone.elements.filter(el => el.id !== elementId)
      }))
    );
  }, [findAndUpdateLayout, paginationEnabled]);

  // Drag and drop handlers
  const handleDragStart = useCallback((e, elementType) => {
    console.log('🚀 handleDragStart called:', { elementType });
    setDraggedElement(elementType);
    e.dataTransfer.effectAllowed = 'copy';
  }, []);

  const handleDragOver = useCallback((e, zoneInfo) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling to parent layouts
    e.dataTransfer.dropEffect = 'copy';
    setDragOverZone(zoneInfo);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverZone(null);
  }, []);

  const handleDrop = useCallback((e, layoutId, zoneId) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling to parent layouts
    console.log('💧 handleDrop called:', { draggedElement, layoutId, zoneId });

    // Only process valid drops with draggedElement and valid layoutId/zoneId
    if (draggedElement && layoutId && zoneId) {
      addElementToZone(draggedElement, layoutId, zoneId);
      setDraggedElement(null);
      setDragOverZone(null);
    } else {
      console.log('⚠️ Ignoring invalid drop:', { draggedElement, layoutId, zoneId });
    }
  }, [draggedElement, addElementToZone]);

  // Enhanced drag and drop for repositioning elements
  const onDragEnd = useCallback((result) => {
    console.log('🎯 onDragEnd called with result:', result);

    const { destination, source, draggableId } = result;

    // Dropped outside the list
    if (!destination) {
      console.log('❌ Dropped outside list, returning');
      return;
    }

    // Dropped in the same position
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      console.log('❌ Dropped in same position, returning');
      return;
    }

    console.log('🔄 Element repositioning:', { source, destination, draggableId });
    console.log('📊 Current elements before update:', elements.length);

    // Parse source and destination
    const sourceId = source.droppableId;
    const destId = destination.droppableId;

    // Helper function to move element between containers
    const moveElementBetweenContainers = () => {
      console.log('🚀 Moving element between containers', { sourceId, destId });
      setElements(prev => {
        let sourceElement = null;
        let newElements = [...prev];

        console.log('📦 Extracting from source:', sourceId);
        // Extract element from source
        if (sourceId === 'main-canvas') {
          console.log('📤 Extracting from main canvas, index:', source.index);
          // From main canvas
          sourceElement = newElements[source.index];
          newElements.splice(source.index, 1);
          console.log('📤 Extracted element:', sourceElement?.type, sourceElement?.id);
        } else if (sourceId.startsWith('zone-')) {
          // From a zone: format is "zone-layoutId-zoneId"
          const parts = sourceId.split('-');
          const layoutId = parts[1];
          const zoneId = parts.slice(2).join('-'); // Rejoin remaining parts as zoneId may contain dashes
          console.log('📤 Extracting from zone:', { layoutId, zoneId, index: source.index });
          newElements = findAndUpdateLayout(newElements, layoutId, zoneId, (zone) => {
            sourceElement = zone.elements[source.index];
            const newZoneElements = [...zone.elements];
            newZoneElements.splice(source.index, 1);
            console.log('📤 Extracted element from zone:', sourceElement?.type, sourceElement?.id);
            return { ...zone, elements: newZoneElements };
          });
        }

        if (!sourceElement) {
          console.log('❌ No source element found, returning original state');
          return prev;
        }

        console.log('📥 Inserting into destination:', destId);
        // Insert element into destination
        if (destId === 'main-canvas') {
          console.log('📥 Inserting into main canvas, index:', destination.index);
          // To main canvas
          newElements.splice(destination.index, 0, sourceElement);
        } else if (destId.startsWith('zone-')) {
          // To a zone: format is "zone-layoutId-zoneId"
          const parts = destId.split('-');
          const layoutId = parts[1];
          const zoneId = parts.slice(2).join('-'); // Rejoin remaining parts as zoneId may contain dashes
          console.log('📥 Inserting into zone:', { layoutId, zoneId, index: destination.index });
          newElements = findAndUpdateLayout(newElements, layoutId, zoneId, (zone) => {
            const newZoneElements = [...zone.elements];
            newZoneElements.splice(destination.index, 0, sourceElement);
            console.log('📥 Inserted element into zone');
            return { ...zone, elements: newZoneElements };
          });
        }

        console.log('✅ Cross-container move complete');
        return newElements;
      });
    };

    // Same container reordering
    if (sourceId === destId) {
      console.log('🔄 Same container reordering');
      if (sourceId === 'main-canvas') {
        console.log('🎯 Reordering in main canvas');
        // Reorder in main canvas
        setElements(prev => {
          console.log('📝 Main canvas reorder - before:', prev.length);
          const result = Array.from(prev);
          const [removed] = result.splice(source.index, 1);
          result.splice(destination.index, 0, removed);
          console.log('📝 Main canvas reorder - after:', result.length);
          return result;
        });
      } else if (sourceId.startsWith('zone-')) {
        console.log('🎯 Reordering within zone:', sourceId);
        // Reorder within zone
        const parts = sourceId.split('-');
        const layoutId = parts[1];
        const zoneId = parts.slice(2).join('-'); // Rejoin remaining parts as zoneId may contain dashes
        console.log('🔍 Parsed zone info:', { layoutId, zoneId });
        setElements(prev =>
          findAndUpdateLayout(prev, layoutId, zoneId, (zone) => {
            console.log('📝 Zone reorder - before:', zone.elements.length);
            const result = Array.from(zone.elements);
            const [removed] = result.splice(source.index, 1);
            result.splice(destination.index, 0, removed);
            console.log('📝 Zone reorder - after:', result.length);
            return { ...zone, elements: result };
          })
        );
      }
    } else {
      console.log('🔄 Cross-container movement');
      // Cross-container movement
      moveElementBetweenContainers();
    }
  }, [findAndUpdateLayout]);

  // Inline editing handlers
  const startInlineEdit = useCallback((elementId, field, currentValue, isInZone = false, layoutId = null, zoneId = null) => {
    setEditingElement({ elementId, isInZone, layoutId, zoneId });
    setEditingField(field);
    setTempValue(currentValue || '');
  }, []);

  const saveInlineEdit = useCallback(() => {
    if (!editingElement || !editingField) return;

    const updateElementField = (element) => {
      // Handle nested fields for CUSTOM_LIST rows
      if (element.type === ELEMENT_TYPES.CUSTOM_LIST && editingField.startsWith('rows.')) {
        const match = editingField.match(/^rows\.(\d+)\.text$/);
        if (match) {
          const idx = parseInt(match[1], 10);
          const newRows = [...element.rows];
          newRows[idx] = { ...newRows[idx], text: tempValue };
          return { ...element, rows: newRows };
        }
      }
      // Handle nested fields for FAQAccordion
      if (element.type === ELEMENT_TYPES.FAQ_ACCORDION && editingField.startsWith('items.')) {
        const match = editingField.match(/^items\.(\d+)\.(question|answer)$/);
        if (match) {
          const idx = parseInt(match[1], 10);
          const field = match[2];
          const newItems = [...element.items];
          newItems[idx] = { ...newItems[idx], [field]: tempValue };
          return { ...element, items: newItems };
        }
      }
      if (element.type === ELEMENT_TYPES.FAQ_ACCORDION && editingField === 'title') {
        return { ...element, title: tempValue };
      }
      // Handle nested fields for ComparisonTable
      if (element.type === ELEMENT_TYPES.COMPARISON_TABLE && editingField.startsWith('rows.')) {
        const match = editingField.match(/^rows\.(\d+)\.(feature|description)$/);
        if (match) {
          const idx = parseInt(match[1], 10);
          const field = match[2];
          const newRows = [...element.rows];
          newRows[idx] = { ...newRows[idx], [field]: tempValue };
          return { ...element, rows: newRows };
        }
      }
      if (element.type === ELEMENT_TYPES.COMPARISON_TABLE && (editingField === 'title' || editingField === 'productA' || editingField === 'productB')) {
        return { ...element, [editingField]: tempValue };
      }
      // Handle nested fields for TIMELINE_LIST steps
      if (element.type === ELEMENT_TYPES.TIMELINE_LIST && editingField.startsWith('steps.')) {
        const match = editingField.match(/^steps\.(\d+)\.(stageLabel|title|description)$/);
        if (match) {
          const idx = parseInt(match[1], 10);
          const field = match[2];
          const newSteps = [...element.steps];
          newSteps[idx] = { ...newSteps[idx], [field]: tempValue };
          return { ...element, steps: newSteps };
        }
      }
      // Handle nested fields for PROCESS_STEPS steps
      if (element.type === ELEMENT_TYPES.PROCESS_STEPS && editingField.startsWith('steps.')) {
        const match = editingField.match(/^steps\.(\d+)\.(title|description|image)$/);
        if (match) {
          const idx = parseInt(match[1], 10);
          const field = match[2];
          const newSteps = [...element.steps];
          newSteps[idx] = { ...newSteps[idx], [field]: tempValue };
          return { ...element, steps: newSteps };
        }
      }
      // Handle nested fields for TEXT_WITH_RIGHT_IMAGE content
      if (element.type === ELEMENT_TYPES.TEXT_WITH_RIGHT_IMAGE && editingField.startsWith('content.')) {
        const match = editingField.match(/^content\.(text|image)$/);
        if (match) {
          const field = match[1];
          return {
            ...element,
            content: {
              ...element.content,
              [field]: tempValue
            }
          };
        }
      }
      // Handle nested fields for LEFT_IMAGE_TEXT content
      if (element.type === ELEMENT_TYPES.LEFT_IMAGE_TEXT && editingField.startsWith('content.')) {
        const match = editingField.match(/^content\.(text|image)$/);
        if (match) {
          const field = match[1];
          return {
            ...element,
            content: {
              ...element.content,
              [field]: tempValue
            }
          };
        }
      }
      // Handle nested fields for BUTTON_WITH_NOTICE
      if (element.type === ELEMENT_TYPES.BUTTON_WITH_NOTICE && editingField.includes('.')) {
        const [parentField, childField] = editingField.split('.');
        return {
          ...element,
          [parentField]: {
            ...element[parentField],
            [childField]: tempValue
          }
        };
      }
      // Handle direct fields for other elements
      else {
        return { ...element, [editingField]: tempValue };
      }
    };

    if (editingElement.isInZone) {
      // Update element in zone (supports deeply nested layouts)
      if (paginationEnabled) {
        // Pagination mode - update current page
        setPages(prev => prev.map((page, pageIndex) => {
          if (pageIndex === currentPageIndex) {
            return {
              ...page,
              elements: findAndUpdateLayout(page.elements, editingElement.layoutId, editingElement.zoneId, (zone) => ({
                ...zone,
                elements: zone.elements.map(zoneEl => {
                  if (zoneEl.id === editingElement.elementId) {
                    return updateElementField(zoneEl);
                  }
                  return zoneEl;
                })
              }))
            };
          }
          return page;
        }));
      } else {
        // Non-pagination mode - update elements directly
        setElements(prev => findAndUpdateLayout(prev, editingElement.layoutId, editingElement.zoneId, (zone) => ({
          ...zone,
          elements: zone.elements.map(zoneEl => {
            if (zoneEl.id === editingElement.elementId) {
              return updateElementField(zoneEl);
            }
            return zoneEl;
          })
        })));
      }
    } else {
      // Update element in main canvas
      if (paginationEnabled) {
        // Pagination mode - update current page
        setPages(prev => prev.map((page, pageIndex) => {
          if (pageIndex === currentPageIndex) {
            return {
              ...page,
              elements: page.elements.map(el =>
                el.id === editingElement.elementId
                  ? updateElementField(el)
                  : el
              )
            };
          }
          return page;
        }));
      } else {
        // Non-pagination mode - update elements directly
        setElements(prev => prev.map(el =>
          el.id === editingElement.elementId
            ? updateElementField(el)
            : el
        ));
      }
    }

    // Update selectedElement if it matches the edited element to sync with Content tab
    if (selectedElement?.id === editingElement.elementId) {
      const updatedSelectedElement = updateElementField(selectedElement);
      console.log('🔄 Syncing selectedElement after inline edit:', updatedSelectedElement);
      setSelectedElement(updatedSelectedElement);
    }

    setEditingElement(null);
    setEditingField(null);
    setTempValue('');
    // Reset text editor initialization flag for next editing session
    window.textEditorInitialized = false;
  }, [editingElement, editingField, tempValue, selectedElement, paginationEnabled, currentPageIndex]);

  const cancelInlineEdit = useCallback(() => {
    setEditingElement(null);
    setEditingField(null);
    setTempValue('');
    // Reset text editor initialization flag for next editing session
    window.textEditorInitialized = false;
  }, []);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      saveInlineEdit();
    } else if (e.key === 'Escape') {
      cancelInlineEdit();
    }
  }, [saveInlineEdit, cancelInlineEdit]);

  // Drag handlers for popover
  const handleMouseDown = useCallback((e) => {
    if (e.target.closest('[data-draggable-handle]')) {
      setIsDragging(true);
      const rect = e.currentTarget.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      // Keep popover within viewport bounds
      const maxX = window.innerWidth - 520; // 500px width + some margin
      const maxY = window.innerHeight - 100; // Some margin from bottom

      setPopoverPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    }
  }, [isDragging, dragOffset]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleResizeMove = useCallback((e) => {
    if (!isResizingRef.current || !resizeStartDataRef.current) {
      return;
    }

    const deltaX = e.clientX - resizeStartDataRef.current.startX;
    const deltaY = e.clientY - resizeStartDataRef.current.startY;

    let newWidth = resizeStartDataRef.current.startWidth;
    let newHeight = resizeStartDataRef.current.startHeight;

    // Calculate new dimensions based on resize direction
    switch (resizeDirectionRef.current) {
      case 'se':
        newWidth = Math.max(50, resizeStartDataRef.current.startWidth + deltaX);
        newHeight = Math.max(50, resizeStartDataRef.current.startHeight + deltaY);
        break;
      case 'sw':
        newWidth = Math.max(50, resizeStartDataRef.current.startWidth - deltaX);
        newHeight = Math.max(50, resizeStartDataRef.current.startHeight + deltaY);
        break;
      case 'ne':
        newWidth = Math.max(50, resizeStartDataRef.current.startWidth + deltaX);
        newHeight = Math.max(50, resizeStartDataRef.current.startHeight - deltaY);
        break;
      case 'nw':
        newWidth = Math.max(50, resizeStartDataRef.current.startWidth - deltaX);
        newHeight = Math.max(50, resizeStartDataRef.current.startHeight - deltaY);
        break;
      case 'e':
        newWidth = Math.max(50, resizeStartDataRef.current.startWidth + deltaX);
        break;
      case 'w':
        newWidth = Math.max(50, resizeStartDataRef.current.startWidth - deltaX);
        break;
      case 's':
        newHeight = Math.max(50, resizeStartDataRef.current.startHeight + deltaY);
        break;
      case 'n':
        newHeight = Math.max(50, resizeStartDataRef.current.startHeight - deltaY);
        break;
    }

    // Update element styles
    const updateElementStyles = (element) => ({
      ...element,
      styles: {
        ...element.styles,
        width: `${newWidth}px`,
        height: `${newHeight}px`
      }
    });

    // Find and update the element
    const updateElementInArray = (elements) => {
      return elements.map(el => {
        if (el.id === resizeStartDataRef.current.elementId) {
          return updateElementStyles(el);
        }
        // Check if element is in a layout zone or image overlay
        if ((el.type === ELEMENT_TYPES.LAYOUT || el.type === ELEMENT_TYPES.IMAGE_OVERLAY) && el.zones) {
          return {
            ...el,
            zones: el.zones.map(zone => ({
              ...zone,
              elements: zone.elements.map(zoneEl =>
                zoneEl.id === resizeStartDataRef.current.elementId
                  ? updateElementStyles(zoneEl)
                  : zoneEl
              )
            }))
          };
        }
        return el;
      });
    };

    setElements(prev => updateElementInArray(prev));

    // Update selectedElement if it matches
    if (selectedElement?.id === resizeStartDataRef.current.elementId) {
      setSelectedElement(updateElementStyles(selectedElement));
    }
  }, [selectedElement]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    setResizeDirection(null);
    setResizeStartData(null);
    isResizingRef.current = false;
    resizeDirectionRef.current = null;
    resizeStartDataRef.current = null;

    // Remove global event listeners
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  }, [handleResizeMove]);

  const handleResizeStart = useCallback((e, element, direction) => {
    console.log('🔴 Resize start:', { direction, elementId: element.id });
    e.preventDefault();
    e.stopPropagation();

    setIsResizing(true);
    setResizeDirection(direction);
    isResizingRef.current = true;
    resizeDirectionRef.current = direction;

    // Get the image element to measure its actual size
    const imageElement = e.currentTarget.closest('.resize-handles')?.previousElementSibling;
    const imageRect = imageElement?.getBoundingClientRect() || e.currentTarget.getBoundingClientRect();

    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = imageRect.width;
    const startHeight = imageRect.height;

    console.log('🔴 Resize data:', { startX, startY, startWidth, startHeight });

    const resizeData = {
      startX,
      startY,
      startWidth,
      startHeight,
      elementId: element.id
    };

    setResizeStartData(resizeData);
    resizeStartDataRef.current = resizeData;

    // Add global event listeners
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  }, [handleResizeMove, handleResizeEnd]);

  // Add/remove global event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'grabbing';
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = 'default';
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const deleteElement = useCallback((elementId) => {
    // Find the element to be deleted
    const elementToDelete = elements.find(el => el.id === elementId);

    if (elementToDelete) {
      // Find the original index of the element in the main canvas
      const originalIndex = elements.findIndex(el => el.id === elementId);

      let pageIndex = null;
      let indexInPage = null;

      // If pagination is enabled, find which page and position within that page
      if (paginationEnabled) {
        for (let i = 0; i < pages.length; i++) {
          const idx = pages[i].elements.findIndex(el => el.id === elementId);
          if (idx !== -1) {
            pageIndex = i;
            indexInPage = idx;
            break;
          }
        }
      }

      // Add to deletion history for undo functionality
      console.log('🔄 Adding to deletion history:', elementToDelete, 'at index:', originalIndex, 'pageIndex:', pageIndex, 'indexInPage:', indexInPage);
      setDeletedElementsHistory(prev => [...prev, {
        element: elementToDelete,
        zone: null, // Main canvas element, no zone
        originalIndex: originalIndex,
        pageIndex: pageIndex,
        indexInPage: indexInPage,
        timestamp: Date.now()
      }]);
      setShowUndoNotification(true);
    }

    if (paginationEnabled) {
      // Pagination mode - update pages state
      setPages(prev => prev.map(page => ({
        ...page,
        elements: page.elements.filter(el => el.id !== elementId)
      })));
    }

    // Always update flat elements list for consistency
    setElements(prev => prev.filter(el => el.id !== elementId));

    if (selectedElement?.id === elementId) {
      setSelectedElement(null);
    }
  }, [selectedElement, elements, paginationEnabled, pages]);

  // Undo delete functionality
  const undoDelete = useCallback(() => {
    if (deletedElementsHistory.length > 0) {
      // Get the most recent deletion
      const lastDeleted = deletedElementsHistory[deletedElementsHistory.length - 1];

      if (lastDeleted.zone) {
        // Restore to original zone at original position
        console.log('🔄 Restoring element to zone at index:', lastDeleted.originalIndex);

        if (paginationEnabled) {
          setPages(prevPages => prevPages.map(page => ({
            ...page,
            elements: findAndUpdateLayout(page.elements, lastDeleted.zone.layoutId, lastDeleted.zone.zoneId, (zone) => {
              const newElements = [...zone.elements];
              newElements.splice(lastDeleted.originalIndex, 0, lastDeleted.element);
              return { ...zone, elements: newElements };
            })
          })));
        }

        setElements(prev =>
          findAndUpdateLayout(prev, lastDeleted.zone.layoutId, lastDeleted.zone.zoneId, (zone) => {
            const newElements = [...zone.elements];
            newElements.splice(lastDeleted.originalIndex, 0, lastDeleted.element);
            return {
              ...zone,
              elements: newElements
            };
          })
        );
      } else {
        // Restore to main canvas at original position
        console.log('🔄 Restoring element to main canvas at index:', lastDeleted.originalIndex, 'pageIndex:', lastDeleted.pageIndex, 'indexInPage:', lastDeleted.indexInPage);

        if (paginationEnabled && lastDeleted.pageIndex !== null && lastDeleted.indexInPage !== null) {
          // Restore to the exact page and position
          setPages(prevPages => {
            return prevPages.map((page, index) => {
              if (index === lastDeleted.pageIndex) {
                const newElements = [...page.elements];
                // Insert at the original position within the page
                newElements.splice(lastDeleted.indexInPage, 0, lastDeleted.element);
                return { ...page, elements: newElements };
              }
              return page;
            });
          });
        }

        setElements(prev => {
          const newElements = [...prev];
          newElements.splice(lastDeleted.originalIndex, 0, lastDeleted.element);
          return newElements;
        });
      }

      // Remove from history
      setDeletedElementsHistory(prev => prev.slice(0, -1));

      // Hide notification if no more deletions to undo
      if (deletedElementsHistory.length === 1) {
        setShowUndoNotification(false);
      }

      console.log('✅ Element restored to original location:', lastDeleted.element.id);
    }
  }, [deletedElementsHistory, findAndUpdateLayout, paginationEnabled]);

  // Dismiss undo notification
  const dismissUndoNotification = useCallback(() => {
    setShowUndoNotification(false);
    setDeletedElementsHistory([]);
  }, []);

  const updateElement = useCallback((elementId, updates, isInZone = false, layoutId = null, zoneId = null, skipSelectedSync = false) => {
    console.log('🔄 updateElement called:', { elementId, updates, isInZone, layoutId, zoneId });
    console.log('🎯 Current selectedElement:', selectedElement?.id);
    console.log('📄 Pagination enabled:', paginationEnabled);

    if (paginationEnabled) {
      // Pagination mode - update pages state
      if (isInZone && layoutId && zoneId) {
        // Update element within a layout zone in pagination mode
        console.log('🔄 Updating zone element in pagination mode');
        setPages(prev => prev.map(page => ({
          ...page,
          elements: findAndUpdateLayout(page.elements, layoutId, zoneId, (zone) => ({
            ...zone,
            elements: zone.elements.map(zoneElement => {
              if (zoneElement.id === elementId) {
                const updatedElement = { ...zoneElement, ...updates };
                console.log('✅ Updated zone element in pagination:', updatedElement);
                console.log('🖼️ imageSrc preserved:', !!updatedElement.imageSrc);
                return updatedElement;
              }
              return zoneElement;
            })
          }))
        })));
      } else {
        // Update top-level element in pagination mode
        setPages(prev => prev.map(page => ({
          ...page,
          elements: page.elements.map(el => {
            if (el.id === elementId) {
              const updatedElement = { ...el, ...updates };
              console.log('✅ Updated paginated element:', updatedElement);
              console.log('🎨 Updated element styles:', updatedElement.styles);
              return updatedElement;
            }
            return el;
          })
        })));
      }
    } else {
      // Non-pagination mode - update elements directly
      setElements(prev => {
        console.log('📝 Previous elements:', prev);

        if (isInZone && layoutId && zoneId) {
          // Update element within a layout zone (supports deeply nested layouts)
          const result = findAndUpdateLayout(prev, layoutId, zoneId, (zone) => ({
            ...zone,
            elements: zone.elements.map(zoneElement => {
              if (zoneElement.id === elementId) {
                const updatedElement = { ...zoneElement, ...updates };
                console.log('✅ Updated nested zone element:', updatedElement);
                return updatedElement;
              }
              return zoneElement;
            })
          }));
          console.log('📋 Updated elements array (nested zone):', result);
          return result;
        } else {
          // Update element in main elements array
          const result = prev.map(el => {
            if (el.id === elementId) {
              const updatedElement = { ...el, ...updates };
              console.log('✅ Updated main element:', updatedElement);
              console.log('🎨 Updated element styles:', updatedElement.styles);
              return updatedElement;
            }
            return el;
          });
          console.log('📋 Updated elements array (main):', result);
          return result;
        }
      });
    }

    // Update selectedElement if it exists and matches
    if (!skipSelectedSync && selectedElement?.id === elementId) {
      const newSelectedElement = { ...selectedElement, ...updates };
      console.log('🎯 Updating selectedElement:', newSelectedElement);
      console.log('🎨 New selectedElement styles:', newSelectedElement.styles);
      console.log('🎨 New selectedElement textStyles:', newSelectedElement.textStyles);
      setSelectedElement(newSelectedElement);

      // Only force a full re-render for visual property changes that need canvas DOM rebuild.
      if (updates.styles || updates.productAImage || updates.productBImage || updates.content || updates.videoFile) {
        setTimeout(() => {
          setForceRender(prev => prev + 1);
        }, 0);
      }
    }

    // Force re-render for image updates, video updates, and style changes
    if (updates.styles || updates.productAImage || updates.productBImage || updates.content || updates.videoFile) {
      console.log('🔄 Forcing re-render due to content/image/video/style changes');
      console.log('🔄 Updates that triggered re-render:', updates);
      setForceRender(prev => {
        const newValue = prev + 1;
        console.log('🔄 forceRender updated from', prev, 'to', newValue);
        return newValue;
      });
    }
  }, [selectedElement, findAndUpdateLayout, setElements, setSelectedElement, paginationEnabled]);

  // Content tab editing handlers
  const saveContentTabEdit = useCallback(() => {
    if (!selectedElement || contentTabTempValue === '') return;

    updateElement(selectedElement.id, { content: contentTabTempValue });
    setContentTabTempValue('');
  }, [selectedElement, contentTabTempValue, updateElement]);

  const cancelContentTabEdit = useCallback(() => {
    setContentTabTempValue('');
  }, []);

  const handleContentTabKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      saveContentTabEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelContentTabEdit();
    }
  }, [saveContentTabEdit, cancelContentTabEdit]);

  // Button Content tab editing handlers
  const saveButtonContentTabEdit = useCallback(() => {
    if (!selectedElement || buttonContentTabTempValue === '') return;

    updateElement(
      selectedElement.id,
      { content: buttonContentTabTempValue },
      selectedElementContext?.isInZone,
      selectedElementContext?.layoutId,
      selectedElementContext?.zoneId
    );
    setButtonContentTabTempValue('');
  }, [selectedElement, buttonContentTabTempValue, updateElement, selectedElementContext]);

  const cancelButtonContentTabEdit = useCallback(() => {
    setButtonContentTabTempValue('');
  }, []);

  const handleButtonContentTabKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      saveButtonContentTabEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelButtonContentTabEdit();
    }
  }, [saveButtonContentTabEdit, cancelButtonContentTabEdit]);

  // Button URL tab editing handlers
  const saveButtonUrlTabEdit = useCallback(() => {
    if (!selectedElement || buttonUrlTabTempValue === '') return;

    updateElement(
      selectedElement.id,
      { url: buttonUrlTabTempValue },
      selectedElementContext?.isInZone,
      selectedElementContext?.layoutId,
      selectedElementContext?.zoneId
    );
    setButtonUrlTabTempValue('');
  }, [selectedElement, buttonUrlTabTempValue, updateElement, selectedElementContext]);

  const cancelButtonUrlTabEdit = useCallback(() => {
    setButtonUrlTabTempValue('');
  }, []);

  const handleButtonUrlTabKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      saveButtonUrlTabEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelButtonUrlTabEdit();
    }
  }, [saveButtonUrlTabEdit, cancelButtonUrlTabEdit]);

  const moveElement = useCallback((elementId, direction) => {
    if (paginationEnabled) {
      // Pagination mode - allow moving across pages
      setPages(prev => {
        // Find which page contains the element
        let sourcePageIndex = -1;
        let elementIndex = -1;

        for (let i = 0; i < prev.length; i++) {
          const idx = prev[i].elements.findIndex(el => el.id === elementId);
          if (idx !== -1) {
            sourcePageIndex = i;
            elementIndex = idx;
            break;
          }
        }

        if (sourcePageIndex === -1 || elementIndex === -1) return prev;

        const sourcePage = prev[sourcePageIndex];
        const element = sourcePage.elements[elementIndex];

        if (direction === 'up') {
          // Moving up
          if (elementIndex > 0) {
            // Move within same page
            const newElements = [...sourcePage.elements];
            [newElements[elementIndex - 1], newElements[elementIndex]] = [newElements[elementIndex], newElements[elementIndex - 1]];
            return prev.map((page, idx) =>
              idx === sourcePageIndex ? { ...page, elements: newElements } : page
            );
          } else if (sourcePageIndex > 0) {
            // Move to previous page (last position)
            const prevPage = prev[sourcePageIndex - 1];
            const newPrevPageElements = [...prevPage.elements, element];
            const newSourcePageElements = sourcePage.elements.slice(1);

            return prev.map((page, idx) => {
              if (idx === sourcePageIndex - 1) {
                return { ...page, elements: newPrevPageElements };
              } else if (idx === sourcePageIndex) {
                return { ...page, elements: newSourcePageElements };
              }
              return page;
            });
          }
        } else {
          // Moving down
          if (elementIndex < sourcePage.elements.length - 1) {
            // Move within same page
            const newElements = [...sourcePage.elements];
            [newElements[elementIndex], newElements[elementIndex + 1]] = [newElements[elementIndex + 1], newElements[elementIndex]];
            return prev.map((page, idx) =>
              idx === sourcePageIndex ? { ...page, elements: newElements } : page
            );
          } else if (sourcePageIndex < prev.length - 1) {
            // Move to next page (first position)
            const nextPage = prev[sourcePageIndex + 1];
            const newSourcePageElements = sourcePage.elements.slice(0, -1);
            const newNextPageElements = [element, ...nextPage.elements];

            return prev.map((page, idx) => {
              if (idx === sourcePageIndex) {
                return { ...page, elements: newSourcePageElements };
              } else if (idx === sourcePageIndex + 1) {
                return { ...page, elements: newNextPageElements };
              }
              return page;
            });
          }
        }

        return prev;
      });
    } else {
      // Regular mode - update elements directly
      setElements(prev => {
        const currentIndex = prev.findIndex(el => el.id === elementId);
        if (currentIndex === -1) return prev;

        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        if (newIndex < 0 || newIndex >= prev.length) return prev;

        const newElements = [...prev];
        [newElements[currentIndex], newElements[newIndex]] = [newElements[newIndex], newElements[currentIndex]];
        return newElements;
      });
    }
  }, [paginationEnabled, currentPageIndex, setElements]);

  return {
    findAndUpdateLayout,
    addLayout,
    addElementToZone,
    deleteElementFromZone,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    onDragEnd,
    startInlineEdit,
    saveInlineEdit,
    cancelInlineEdit,
    handleKeyPress,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleResizeMove,
    handleResizeEnd,
    handleResizeStart,
    deleteElement,
    undoDelete,
    dismissUndoNotification,
    updateElement,
    saveContentTabEdit,
    cancelContentTabEdit,
    handleContentTabKeyPress,
    saveButtonContentTabEdit,
    cancelButtonContentTabEdit,
    handleButtonContentTabKeyPress,
    saveButtonUrlTabEdit,
    cancelButtonUrlTabEdit,
    handleButtonUrlTabKeyPress,
    moveElement,
  };
};
